import React from 'react';
import { Box } from '@mui/material';
import {
  FormDialog,
  FormSection,
  RhfSelect,
  RhfTextField,
  toast,
} from '@core/components';
import { CustomerTitle } from '@core/enum/customerTitle';
import { getOptions } from '@core/utils/enum';
import { Diocese } from '@apps/leitourgos-vestments/api/diocese';
import type { Customer } from '../../api/customer';
import { useAppContext } from '../context/AppContext';
import {
  type CustomerFormValues,
  useCustomerForm,
} from '../hooks/useCustomerForm';
import { CustomerMeasurementFields } from './CustomerMeasurementFields';

interface CustomerDialogProps {
  open: boolean;
  onClose: () => void;
  customer?: Customer | null;
  inputValue?: string;
  onSuccess?: (customer: Customer) => void;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  open,
  onClose,
  customer,
  inputValue,
  onSuccess,
}) => {
  const { dioceses } = useAppContext();
  const { defaultValues, save } = useCustomerForm(customer, inputValue);

  const onSubmit = async (data: CustomerFormValues) => {
    const saved = await save(data);
    toast.success(
      customer
        ? 'Customer updated successfully'
        : 'Customer created successfully'
    );
    onSuccess?.(saved);
  };

  return (
    <FormDialog<CustomerFormValues>
      open={open}
      onClose={onClose}
      title={customer ? 'Edit Customer' : 'Add Customer'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText="Save"
      cancelText="Cancel"
    >
      <FormSection title="Personal Information">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfSelect<CustomerFormValues>
            name="title"
            label="Title"
            sx={{ width: { xs: '100%', md: '120px' } }}
            options={getOptions(CustomerTitle)}
          />
          <RhfTextField<CustomerFormValues> name="name" label="Name" required />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfSelect<CustomerFormValues, Diocese>
            name="diocese"
            label="Diocese"
            items={dioceses}
            getOptionValue={(diocese) => String(diocese.id)}
            getOptionLabel={(diocese) => diocese.name}
          />
          <RhfTextField<CustomerFormValues> name="address" label="Address" />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfTextField<CustomerFormValues>
            name="email"
            label="Email"
            textFieldProps={{
              type: 'email',
            }}
          />
          <RhfTextField<CustomerFormValues>
            name="phone"
            label="Phone"
            textFieldProps={{
              type: 'tel',
            }}
          />
        </Box>
      </FormSection>
      <FormSection title="Alb measurements">
        <CustomerMeasurementFields
          group="alb"
          columns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }}
        />
      </FormSection>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <FormSection
          title="Chasuble measurements"
          sx={{
            flex: { xs: '1 1 auto', md: '3 1 0' },
          }}
        >
          <CustomerMeasurementFields
            group="chasuble"
            columns={{ xs: '1fr 1fr', md: 'repeat(2, 1fr)' }}
            sx={{ width: '71.08%' }}
          />
        </FormSection>
        <FormSection
          title="Mitre measurements"
          sx={{
            flex: { xs: '1 1 auto', md: '1 1 0' },
          }}
        >
          <CustomerMeasurementFields
            group="mitre"
            columns={{ xs: '1fr 1fr', md: '1fr' }}
          />
        </FormSection>
      </Box>
    </FormDialog>
  );
};

export default CustomerDialog;
